/**
 * The payout-week stat cards' read: every contract in the week, not the table's page.
 *
 * THE CARDS USED TO ADD UP ONE PAGE. `PartnerPayoutWeek` fetched `get_partner_week_contracts` 15
 * rows at a time for its table and computed Total Amount, Vehicles, Avg per Vehicle, Top Earner and
 * Payout Status from that same page. Primelane pays on about 85 contracts a week (102 at most, and
 * 52 of the last 53 weeks had more than 15). On production for the week of 12 Oct 2026 the cards read
 * GHS 18,145.87 over 15 vehicles; the week holds GHS 134,122.73 over 95. Payout Status could say
 * "paid" while unpaid contracts sat on a later page, and turning the page changed every card. A wrong
 * partner payout is money owed to a real counterparty.
 *
 * THE SAME RPC, SO EVERY FIGURE MEANS WHAT IT MEANT. The cards read the RPC the table reads, with
 * the same partner and week and no search, so `partner_portion` and `status` are the server's own
 * values. Only the set of rows changes: the week instead of a page of it. The table keeps its
 * 15-row page.
 *
 * HOW IT READS THE WHOLE WEEK. `p_page_size: null` makes the function's `LIMIT p_page_size` a
 * `LIMIT NULL`, which PostgreSQL defines as no limit (and `OFFSET NULL` as no offset). The function
 * orders by the latest agreement's `signed_at`, which is not unique, so the paging is done by
 * PostgREST on top of the function's result, ordered by `contract_id`. Each row is one contract (the
 * body groups by `c.id`, and every joined table is one row per contract or per vehicle), so that key
 * is unique. `fetchAllRows` pages on it until the exact count is reached, whatever Max rows is.
 *
 * TWO COUNTS, AND BOTH MUST AGREE. PostgREST's exact count is taken over what the function returned.
 * If a later version of the body read a NULL page size as its default of 50, that count would agree
 * with the 50 rows that came back and the week would still be short. The function also counts its
 * own rows with `COUNT(*) OVER ()` before its LIMIT, as `total_contracts` on every row, so the read
 * checks that count as well and throws when the two disagree.
 */

import { supabase } from '@/integrations/supabase/client';
import { assertComplete, fetchAllRows } from '@/lib/fetchAllRows';

export interface WeekContract {
    contract_id: number;
    vehicle_reg: string | null;
    total_paid: number;
    management_rate: number;
    partner_portion: number;
    payout_amount: number;
    status: string;
    vehicle_listing_url: string | null;
    vehicle_color: string | null;
    vehicle_updated_at: string | null;
    total_contracts: number;
    vehicle_make: string | null;
    vehicle_model: string | null;
    payout_date: string | null;
}

export interface PayoutWeekStats {
    totalAmount: number;
    vehicleCount: number;
    avgPerVehicle: number;
    topEarner: WeekContract | null;
    overallStatus: string;
}

const SUBJECT = 'contracts for this payout week';

/** Every contract with a repayment in the week, ordered by `contract_id`. */
export async function fetchPayoutWeekContracts(
    partnerId: number,
    fromInstant: string,
    toInstant: string,
): Promise<WeekContract[]> {
    const rows = await fetchAllRows<WeekContract>({
        label: SUBJECT,
        orderedBy: 'contract_id',
        page: (from, to) =>
            supabase
                .rpc(
                    'get_partner_week_contracts',
                    {
                        p_partner_id: partnerId,
                        p_from_date: fromInstant,
                        p_to_date: toInstant,
                        p_page: 1,
                        // NULL is what lifts the function's LIMIT; the generated Args type only knows a number.
                        p_page_size: null as unknown as number,
                    },
                    { count: 'exact' },
                )
                .order('contract_id', { ascending: true })
                .range(from, to),
        keyOf: (contract) => contract.contract_id,
    });

    // The function's own count, taken before its LIMIT. Every row carries the same value.
    const counted = rows.reduce((most, r) => Math.max(most, r.total_contracts ?? 0), 0);
    assertComplete(rows, counted, SUBJECT, "the payout RPC returned fewer rows than it counted");
    return rows;
}

/**
 * The five cards, over whatever rows they are given. The arithmetic is the page's own, moved here
 * unchanged so it can be tested; the caller now passes it the whole week.
 */
export function payoutWeekStats(contracts: readonly WeekContract[]): PayoutWeekStats {
    const totalAmount = contracts.reduce((sum, c) => sum + c.partner_portion, 0);
    const vehicleSet = new Set<string>();
    contracts.forEach(c => { if (c.vehicle_reg) vehicleSet.add(c.vehicle_reg); });
    const vehicleCount = vehicleSet.size;
    const avgPerVehicle = vehicleCount > 0 ? totalAmount / vehicleCount : 0;
    const topEarner = contracts.length > 0
        ? contracts.reduce((best, c) => c.partner_portion > best.partner_portion ? c : best, contracts[0])
        : null;

    // Overall payout status
    const allPaid = contracts.length > 0 && contracts.every(c => c.status === 'paid');
    const anyPaid = contracts.some(c => c.status === 'paid');
    const overallStatus = contracts.length === 0 ? 'upcoming'
        : allPaid ? 'paid'
        : anyPaid ? 'partial'
        : contracts[0]?.status ?? 'due';

    return { totalAmount, vehicleCount, avgPerVehicle, topEarner, overallStatus };
}
